/* eslint-disable react/prop-types */
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from "@azure/msal-react";
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Empty,
  Form,
  Skeleton,
  Space,
  Tabs,
  Tag,
  Typography,
  message,
  theme,
} from "antd";
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  EditOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams, useSearchParams } from "react-router";

import { WigMetadataForm } from "@warwick-investment-group/wig-react-library";

import SignIn from "../signIn";
import { AppContext } from "../../AppContextProvider";
import usePermissions from "../../hooks/usePermissions";
import dealsService from "../../services/dealsService";
import { apiErrorText } from "../../utils/apiError";
import brokerAccessService from "../../services/brokerAccessService";
import useLookupLabels from "../../hooks/useLookupLabels";
import useDealHeaderSummary from "../../hooks/useDealHeaderSummary";
import { formatDateOnly } from "../../utils/dateFormat";
import { serializeDealForm } from "../../utils/serializeDealForm";
import { toFormValues } from "../../utils/toFormValues";
import { filterLookupOptions } from "../../utils/lookupFilter";
import { withAgentPlaceholder } from "../../utils/agentLookupHint";
import { deriveDealTotals } from "../../utils/dealBonusTotal";
import { agentRosterOptions } from "../../utils/brokerAgentRoster";
import useSelectOptionFallback from "../../hooks/useSelectOptionFallback";
import { isRecordingsApplicable } from "../../utils/recordingsApplicable";
import { filterAssignmentOnlyFields } from "../../utils/assignmentOnlyFields";
import {
  filterBrokerHiddenFields,
  isExternalBrokerAgent,
} from "../../utils/brokerHiddenFields";
import { withBrokerRequiredAoi } from "../../utils/brokerRequiredAoi";
import {
  formatV2Value,
  groupMetadataByCategory,
  MUTED_STYLE,
  titleCase,
} from "../../utils/metadataV2Columns";
import {
  GUARDED_SHARE_FIELDS,
  INTEREST_FIELD_RENDERERS,
  ShareBoundsBanner,
} from "../../components/interestInput";
import DeleteDealsConfirmModal from "../../components/deleteDealsConfirmModal";
import EntitiesTab from "./tabs/EntitiesTab";
import StatusHistoryTab from "./tabs/StatusHistoryTab";
import ApprovalsTab from "./tabs/ApprovalsTab";
import ContactLogTab from "./tabs/ContactLogTab";
import AssignmentRecordingsTab from "./tabs/AssignmentRecordingsTab";
import LeaseDeedsTab from "./tabs/LeaseDeedsTab";
import WellsTab from "./tabs/WellsTab";
import PaymentsTab from "./tabs/PaymentsTab";
import RemarksTab from "./tabs/RemarksTab";
import AttachmentsTab from "./tabs/AttachmentsTab";
import DealMap from "../../components/dealMap/DealMap";

const { Title, Text, Paragraph } = Typography;

// Field-level RBAC in the in-place edit form is driven by `canEditField`
// (utils/permissions), which prefers the server's per-row `editable_for_me`
// and falls back to the local mirror of DEAL_FIELD_ROLE_GROUPS. That covers
// assignment and status — which used to be special-cased here — plus the
// handoff dates and operator-notification fields the server also gates.
//
// Fields the role can't write stay VISIBLE but render as their formatted
// value (see `readOnlyRenderers` below), so a landman still sees when title
// reports were integrated without being offered an input whose save 403s.
// They carry no `name`, so they never enter form state or the PUT body.

// Pull a human-readable message out of an axios error, preferring the
// FastAPI `detail` field (where the 409/422 reason lives).
const errText = (err) => apiErrorText(err);

// Tab definitions. Phase 1 implemented "overview"; Phase 2a filled in the
// four deal-side child-entity tabs (entities, status_history, approvals,
// assignment_recordings). Phase 2b added lease_deeds + wells. Phase 2c
// added the three polymorphic tabs (payments, remarks, attachments).
// After Phase 2c, all 10 tabs are live.
const TABS = [
  { key: "overview", label: "Deal", placeholder: null },
  { key: "entities", label: "Entities", placeholder: "deal_entities" },
  { key: "contact_log", label: "Contact Log", placeholder: "deal_contact_log" },
  { key: "lease_deeds", label: "Lease/Deeds", placeholder: "lease_deeds" },
  { key: "wells", label: "Wells", placeholder: "wells" },
  { key: "approvals", label: "Approvals", placeholder: "approvals" },
  { key: "payments", label: "Payments", placeholder: "payments" },
  { key: "status_history", label: "Status", placeholder: "deal_status_history" },
  {
    key: "assignment_recordings",
    label: "Recordings",
    placeholder: "assignment_recordings",
    // Recordings only exist for deals with an assignment to file — hidden
    // for LEASE / DEED / OPTION / SURFACE (see isRecordingsApplicable).
    visibleFor: isRecordingsApplicable,
  },
  { key: "remarks", label: "Remarks", placeholder: "remarks" },
  // This deal's tracts on the PLSS grid (+ neighbouring deals on request).
  // Mounts only while open (destroyInactiveTabPane), so the map's data and
  // Mapbox token load on first open. A preview feature: BD.Admin only for now.
  { key: "map", label: "Map", placeholder: null },
  { key: "attachments", label: "Attachments", placeholder: "attachments" },
];

const VALID_TAB_KEYS = new Set(TABS.map((t) => t.key));

/**
 * Render a single field value for the Overview tab. Wraps the formatter
 * output in a span/paragraph appropriate for the data_type so LongText
 * fields get block layout while everything else stays inline.
 *
 * `lookupLabels` resolves FK-id `Lookup` fields (brokerage_id ->
 * brokers_xref, warwick_landman_id -> warwick_landman, aoi_id -> aois) to
 * their human label. Fields keyed by a lookup whose stored value IS the label
 * (deal_type / deal_status) have no map entry and fall through to raw display.
 */
const RenderedValue = ({ meta, value, lookupLabels }) => {
  let display = value;
  const lk = meta?.distinct_values_lookup;
  if (
    meta?.data_type === "Lookup" &&
    lk &&
    lookupLabels?.[lk] &&
    value != null &&
    value !== ""
  ) {
    const resolved = lookupLabels[lk][value];
    if (resolved != null) display = resolved;
  }
  const fmt = formatV2Value(meta, display);
  if (fmt.kind === "blank") return <span style={MUTED_STYLE}>—</span>;
  if (fmt.kind === "longtext")
    return (
      <Paragraph style={{ marginBottom: 0, whiteSpace: "pre-wrap" }}>
        {fmt.text}
      </Paragraph>
    );
  return <span>{fmt.text}</span>;
};

/**
 * Deal tab body — every visible deal-level field from /metadata_v2, grouped
 * by `category` into Cards. Read-only by default; the Edit button flips the
 * tab into an in-place editable form (no drawer). Save serializes the form
 * and PUTs it via dealsService.updateDeal, then hands the updated row back to
 * the parent so the header / fields refresh (and a renamed deal_key navigates).
 */
const OverviewTab = ({ deal, metadata: dealMetadata, onSaved, onDirtyChange }) => {
  const { token, getToken, isTokenExpired, user } = useContext(AppContext);
  const { can, canEditField } = usePermissions();
  const { token: themeToken } = theme.useToken();
  const [form] = Form.useForm();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  // Track whether the in-place edit form has unsaved changes so the parent
  // page can guard navigation / tab switches (the tabs use
  // destroyInactiveTabPane, so switching away silently discards edits).
  const [dirty, setDirty] = useState(false);

  // Deal-type applicability: the assignment dates / term and the assignment
  // economics (Delivered NRI, Bonus Per Acre, Total Bonus) only exist on an
  // ASSIGNMENT deal. Narrowing the metadata here — rather than at each render
  // site — drops those fields from the read view, the edit form, the
  // read-only-renderer map AND the serialized PUT body in one move, since all
  // four are built off this same list.
  const metadata = useMemo(
    () => filterAssignmentOnlyFields(dealMetadata, deal),
    [dealMetadata, deal]
  );

  const categoryGroups = useMemo(
    () => groupMetadataByCategory(metadata),
    [metadata]
  );

  // Edit mode renders every visible field — WigMetadataForm's default
  // hide/editable predicate only. Role gating happens per field below
  // (read-only render + payload filter) rather than by dropping fields, so
  // the form still shows the full deal.
  const editFieldFilter = useMemo(
    () => (row) => !row.hide && row.editable !== false,
    []
  );

  // Per-field write predicate — the single source of truth for "may this
  // role change this column", used for BOTH the read-only rendering and the
  // serialized body so the two can't disagree.
  const canWriteField = useCallback(
    (field, metaRow) => canEditField(field, metaRow),
    [canEditField]
  );

  // Show Edit if the role can write ANY field on this deal, not just the
  // terms group. `deal.editTerms` alone would lock BD.LeaseRecords /
  // BD.DivisionOrders out of the form entirely — and the handoff dates they
  // own are edited nowhere else, so those roles could never record one.
  // Computed off the metadata so it tracks whatever the server says is
  // writable rather than a second hardcoded role list.
  const canEdit = useMemo(
    () =>
      can("deal.editTerms") ||
      (Array.isArray(metadata) ? metadata : []).some(
        (row) =>
          row?.field &&
          !row.hide &&
          row.editable !== false &&
          canWriteField(row.field, row)
      ),
    [can, metadata, canWriteField]
  );

  // Coerce Date/Timestamp fields to dayjs so the edit form's <DatePicker>s
  // render — the library's WigMetadataForm doesn't do this at runtime, so raw
  // ISO strings otherwise crash the picker (getUDayjs(...).isValid).
  const initialValues = useMemo(() => toFormValues(deal || {}, metadata), [deal, metadata]);

  // FK-id label resolution for the read view (brokerage_id / warwick_landman_id
  // / aoi_id). The deal row carries only the numeric FK, so `labelByLookup`
  // resolves ids -> names. The edit form resolves its own labels via
  // WigMetadataForm's async LookupSelect + the wired lookupFetchers.
  const { labelByLookup: lookupLabels } = useLookupLabels();

  // Read-only renderers for the fields this role may not write. Keyed by
  // field so they override WigMetadataForm's default input mapping — and
  // they intentionally take precedence over INTEREST_FIELD_RENDERERS, since
  // a gated interest field must not render an editable interest input.
  //
  // A `<Form.Item>` with no `name` keeps the label/spacing identical to the
  // real inputs while staying out of form state — so `getFieldsValue` never
  // sees it and it can't reach the PUT body.
  const readOnlyRenderers = useMemo(() => {
    const map = {};
    for (const row of Array.isArray(metadata) ? metadata : []) {
      if (!row?.field) continue;
      if (row.hide || row.editable === false) continue;
      if (canWriteField(row.field, row)) continue;
      map[row.field] = (meta) => (
        <Form.Item
          label={meta.label || titleCase(meta.field)}
          tooltip={meta.definition || undefined}
        >
          <RenderedValue
            meta={meta}
            value={deal?.[meta.field]}
            lookupLabels={lookupLabels}
          />
        </Form.Item>
      );
    }
    return map;
  }, [metadata, canWriteField, deal, lookupLabels]);

  // Clear transient state on entering edit / changing deal. Do NOT call
  // form.resetFields() here: the edit <Form> + WigMetadataForm are mounted only
  // while `editing`, and WigMetadataForm populates the fields from
  // `initialValues` in its own (child) effect. A reset here runs afterwards
  // (parent effects fire after child effects) and wipes the edit form back to
  // empty (UAT #25). Each enter-edit remounts the form fresh, so no stale
  // values leak across opens.
  useEffect(() => {
    if (editing) {
      setErrorMsg(null);
    }
    // Entering edit, leaving edit, or a fresh deal all clear the dirty flag.
    setDirty(false);
  }, [editing, deal]);

  // Surface "unsaved edits in progress" to the parent page so it can confirm
  // before tab switches / back navigation. Clears on unmount.
  useEffect(() => {
    onDirtyChange?.(editing && dirty);
    return () => onDirtyChange?.(false);
  }, [editing, dirty, onDirtyChange]);

  // Warn on hard browser navigation (refresh, close, external link) while an
  // edit is dirty. SPA route changes are guarded separately by the parent.
  useEffect(() => {
    if (!(editing && dirty)) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [editing, dirty]);

  const getAuth = async () => {
    const userOid = user?.idTokenClaims?.oid || user?.localAccountId;
    let tk = token;
    if (!tk || isTokenExpired(tk)) tk = await getToken();
    return { token: tk, userOid };
  };

  // Live brokerage selection, used to scope the `broker_agents` lookup.
  //
  // This MUST be watched rather than only read inside the fetcher: the
  // library's LookupSelect fetches exactly ONCE per fetcher identity
  // (`useEffect(() => runQuery(""), [runQuery])`) and never re-fetches when the
  // dropdown is opened. Child effects run before the parent's, so the agent
  // fetcher used to run at mount — BEFORE WigMetadataForm had seeded the form
  // from `initialValues` — see an empty `brokerage_id`, return [], and stay
  // empty for the life of the edit form (and after re-picking a brokerage).
  // Watching the value gives the Select a NEW fetcher identity, which re-runs
  // that mount effect and re-scopes the options. Mirrors dealFormDrawer.
  const editBrokerageId = Form.useWatch("brokerage_id", form);

  // Lookup fetchers keyed by the metadata's `distinct_values_lookup`, mirroring
  // the create drawer. brokers_xref -> /brokers, warwick_landman -> /landmen,
  // aois -> /aois, deal_status -> /deal_status (id === label). Fields with no
  // fetcher fall back to text.
  const lookupFetchers = useMemo(
    () => {
      // The FK id -> label maps are already loaded for the read view
      // (useLookupLabels -> lookupLabels). Serve the edit-form Selects from
      // that in-memory data so they show the NAME immediately, instead of
      // flashing the raw id for a beat while a fresh network fetch runs. Fall
      // back to a network fetch only until the map has loaded.
      const fromMap = (key) => {
        const map = lookupLabels[key];
        if (!map || Object.keys(map).length === 0) return null;
        return Object.entries(map).map(([id, label]) => ({
          id: /^-?\d+$/.test(id) ? Number(id) : id,
          label,
        }));
      };
      return {
      brokers_xref: async (query) => {
        const cached = fromMap("brokers_xref");
        if (cached) return filterLookupOptions(cached, query);
        const { token: tk, userOid } = await getAuth();
        const resp = await dealsService.listBrokers({ token: tk, userOid });
        const rows = resp?.data?.data || resp?.data || [];
        const opts = Array.isArray(rows)
          ? rows.map((r) => ({
              id: r.id,
              label: r.company_name || r.vendor_code || `Broker ${r.id}`,
            }))
          : [];
        return filterLookupOptions(opts, query);
      },
      warwick_landman: async (query) => {
        const cached = fromMap("warwick_landman");
        if (cached) return filterLookupOptions(cached, query);
        const { token: tk, userOid } = await getAuth();
        const resp = await dealsService.listLandmen({ token: tk, userOid });
        const rows = resp?.data?.data || resp?.data || [];
        const opts = Array.isArray(rows)
          ? rows.map((r) => ({
              id: r.id,
              label: r.full_name || `Landman ${r.id}`,
            }))
          : [];
        return filterLookupOptions(opts, query);
      },
      aois: async (query) => {
        const cached = fromMap("aois");
        if (cached) return filterLookupOptions(cached, query);
        const { token: tk, userOid } = await getAuth();
        const resp = await dealsService.listAois({ token: tk, userOid });
        const rows = resp?.data?.data || resp?.data || [];
        const opts = Array.isArray(rows)
          ? rows.map((r) => ({ id: r.id, label: r.name || `AOI ${r.id}` }))
          : [];
        return filterLookupOptions(opts, query);
      },
      deal_status: async (query) => {
        const { token: tk, userOid } = await getAuth();
        const resp = await dealsService.listDealStatuses({ token: tk, userOid });
        const rows = resp?.data?.data || resp?.data || [];
        const opts = Array.isArray(rows)
          ? rows
              .map((r) => r?.deal_status ?? r?.value ?? r)
              .filter((v) => v != null && v !== "")
              .map((v) => ({ id: v, label: String(v) }))
          : [];
        return filterLookupOptions(opts, query);
      },
      // broker_agents -> broker_agent_access, scoped to the currently-selected
      // brokerage_id (read live off the form so re-selecting a brokerage
      // re-scopes the options). List-only: no brokerage selected => empty. The
      // stored deal value is the agent's NAME (text), so id === label === name.
      // Rows without a name are skipped, as are SUSPENDED agents
      // (`active === false`) — they must not be assignable going forward, but a
      // deal that already stores a suspended agent's name keeps showing it
      // (the value is free text, not resolved through this list).
      // `form` from Form.useForm() is stable.
      broker_agents: async (query) => {
        // Prefer the watched value (this fetcher's identity is keyed to it);
        // fall back to reading the form, then to the deal's own brokerage.
        // That last fallback is what serves a BD.BrokerAgent: `brokerage_id`
        // is not writable for them, so it renders as a static value with no
        // `name` and never enters form state — leaving the roster with nothing
        // to scope to. The deal already carries the answer.
        const brokerageId =
          editBrokerageId ??
          form.getFieldValue("brokerage_id") ??
          deal?.brokerage_id;
        if (brokerageId == null || brokerageId === "") return [];
        const { token: tk, userOid } = await getAuth();
        const resp = await brokerAccessService.listBrokerAccess({
          token: tk,
          userOid,
        });
        const rows = resp?.data?.data || resp?.data || [];
        return filterLookupOptions(
          agentRosterOptions(rows, brokerageId),
          query
        );
      },
      };
    },
    // Rebuild when the label maps finish loading so the in-memory options take
    // over from the network fallback, and whenever the selected brokerage
    // changes so the agent Select re-fetches its (brokerage-scoped) options —
    // see the note on `editBrokerageId` above. getAuth closes over auth
    // context; `form` is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lookupLabels, editBrokerageId, deal?.brokerage_id]
  );

  // Same brokerage-aware hint the create drawer stamps on the agent row —
  // edit-only, so the read view and the PUT body keep using `metadata`. On an
  // existing deal there is always a brokerage in scope (the deal's own), so
  // this resolves to the scoped hint rather than the "pick one first" prompt.
  const editMetadata = useMemo(() => {
    const brokerageRow =
      (Array.isArray(metadata) ? metadata : []).find(
        (m) => m?.field === "brokerage_id"
      ) || null;
    return withAgentPlaceholder(metadata, editBrokerageId ?? deal?.brokerage_id, {
      canPickBrokerage: canWriteField("brokerage_id", brokerageRow),
    });
  }, [metadata, editBrokerageId, deal?.brokerage_id, canWriteField]);

  // Safety net for the other dropdowns — see hooks/useSelectOptionFallback.
  const { metadata: editMetadataFinal, lookupFetchers: lookupFetchersFinal } =
    useSelectOptionFallback(editMetadata, lookupFetchers, "deals");

  const handleCancel = () => {
    setEditing(false);
    setErrorMsg(null);
  };

  const handleSave = async () => {
    setErrorMsg(null);
    try {
      // Metadata `required` rules plus the field-level bounds rules
      // (Delivered NRI ≤ 1 — see components/interestInput.jsx).
      await form.validateFields();
    } catch (err) {
      // AntD highlights the offending fields inline; also surface them in the
      // sticky Alert and scroll the first one into view — the edit form spans
      // several category cards, so the failing field is often off-screen.
      const fields = err?.errorFields || [];
      if (fields.length) {
        setErrorMsg(
          fields
            .flatMap((f) => f.errors || [])
            .filter(Boolean)
            .join(" ")
        );
        form.scrollToField(fields[0].name, { block: "center" });
      }
      return;
    }

    const values = form.getFieldsValue(true);
    // Same predicate that decided what rendered as an input — a field this
    // role can't write is omitted rather than echoed back unchanged, which
    // the server's field-level guard would reject for the whole save.
    const body = serializeDealForm(values, metadata, canWriteField);

    setSaving(true);
    try {
      const { token: tk, userOid } = await getAuth();
      const resp = await dealsService.updateDeal({
        token: tk,
        userOid,
        dealKey: deal?.deal_key,
        body,
      });
      const updated = resp?.data?.data ?? resp?.data ?? { ...deal, ...body };
      message.success("Deal updated");
      setEditing(false);
      onSaved?.({ mode: "edit", deal: updated, body });
    } catch (err) {
      const status = err?.response?.status;
      const msg = errText(err);
      setErrorMsg(
        status === 409
          ? `Duplicate deal key: ${msg}`
          : status === 422
            ? `Validation error: ${msg}`
            : msg
      );
    } finally {
      setSaving(false);
    }
  };

  if (!deal) return <Empty description="No deal data" />;
  if (!categoryGroups.length)
    return <Empty description="No metadata available" />;

  const cardTitle = (cat, count) => (
    <Space>
      <Text strong>{cat}</Text>
      <Tag style={{ marginInlineStart: 4 }}>{count}</Tag>
    </Space>
  );

  // The app sets `#root { text-align: center }`, which centers the AntD card
  // head title. Left-align section headers so they read as labels, not banners.
  const cardStyles = { header: { textAlign: "left" } };

  return (
    <div style={{ width: "100%" }}>
      {/* Sticky action bar — stays visible while the deal fields scroll
          beneath it, so Edit / Save / Cancel are always reachable. */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 2,
          display: "flex",
          justifyContent: "flex-end",
          gap: 8,
          padding: "4px 0 12px",
          background: themeToken.colorBgContainer,
        }}
      >
        {editing ? (
          <>
            <Button onClick={handleCancel} disabled={saving}>
              Cancel
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
              onClick={handleSave}
            >
              Save
            </Button>
          </>
        ) : (
          canEdit && (
            <Button icon={<EditOutlined />} onClick={() => setEditing(true)}>
              Edit
            </Button>
          )
        )}
      </div>

      {errorMsg && (
        <Alert
          type="error"
          showIcon
          message="Failed to save deal"
          description={errorMsg}
          style={{ marginBottom: 16 }}
        />
      )}

      {editing ? (
        <Form
          form={form}
          layout="vertical"
          style={{ width: "100%" }}
          // Changing the brokerage re-scopes the agent roster, so a name
          // picked under the previous brokerage is no longer valid — drop it
          // rather than save an agent who doesn't work for the selected firm.
          // Programmatic setFieldsValue (how WigMetadataForm seeds the edit
          // form) does not fire this, so initial population is unaffected.
          onValuesChange={(changed, all) => {
            setDirty(true);
            if (
              Object.prototype.hasOwnProperty.call(changed, "brokerage_id")
            ) {
              form.setFieldValue("agent", undefined);
            }
            // Total Bonus follows $/ac × net acres delivered (feedback #39).
            // A default, not a computed column: it fills as the inputs change
            // and the user can type over the result. Same reason the agent
            // reset above is safe — programmatic seeding does not fire this,
            // so opening a deal never rewrites its stored total.
            const totals = deriveDealTotals(changed, all);
            if (Object.keys(totals).length) form.setFieldsValue(totals);
          }}
        >
          {categoryGroups.map(([cat, fields]) => (
            <Card
              key={cat}
              size="small"
              style={{ marginBottom: 16 }}
              styles={cardStyles}
              title={cardTitle(cat, fields.length)}
            >
              {/* One full-width banner at the top of the section listing every
                  out-of-range share field in it (Delivered NRI lives in Terms).
                  Scoped to this card's fields so the Terms warning doesn't
                  appear above an unrelated section. */}
              <ShareBoundsBanner
                fields={fields
                  .map((f) => f.field)
                  .filter((f) => GUARDED_SHARE_FIELDS.includes(f))}
              />
              <WigMetadataForm
                metadata={editMetadataFinal}
                initialValues={initialValues}
                form={form}
                columns={2}
                fieldFilter={(row) =>
                  editFieldFilter(row) && (row.category || "Other") === cat
                }
                lookupFetchers={lookupFetchersFinal}
                customRenderers={{
                  ...INTEREST_FIELD_RENDERERS,
                  ...readOnlyRenderers,
                }}
              />
            </Card>
          ))}
        </Form>
      ) : (
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          {categoryGroups.map(([cat, fields]) => (
            <Card
              key={cat}
              size="small"
              styles={cardStyles}
              title={cardTitle(cat, fields.length)}
            >
              <Descriptions
                column={{ xs: 1, sm: 1, md: 2, lg: 2, xl: 3 }}
                size="small"
              >
                {fields.map((m) => {
                  const label = (
                    <Space size={4}>
                      <span>{m.label || titleCase(m.field)}</span>
                      {m.is_pii ? (
                        <Tag color="gold" style={{ margin: 0, fontSize: 10 }}>
                          PII
                        </Tag>
                      ) : null}
                    </Space>
                  );
                  return (
                    <Descriptions.Item key={m.field} label={label}>
                      <RenderedValue
                        meta={m}
                        value={deal?.[m.field]}
                        lookupLabels={lookupLabels}
                      />
                    </Descriptions.Item>
                  );
                })}
              </Descriptions>
            </Card>
          ))}
        </Space>
      )}
    </div>
  );
};

/**
 * "Coming soon" placeholder for the other 8 tabs. Names the entity table
 * the future tab will surface, so the user knows what data lives there.
 */
const ComingSoonTab = ({ label, entity }) => (
  <Card size="small">
    <Empty
      description={
        <Space direction="vertical">
          <Text>{label} — coming soon</Text>
          {entity ? (
            <Text type="secondary">
              Will list rows from <code>{entity}</code>.
            </Text>
          ) : null}
        </Space>
      }
    />
  </Card>
);

/**
 * A small header card summarizing the deal. The first four values sit on the
 * deal row; Seller / County / State / Effective Date are derived from the
 * deal's entities, lease/deeds and tracts (feedback #70 — the row's legacy
 * lessor_assignor / county / state / effective_date columns no longer exist).
 * Falls back to muted "—" so the card stays consistent for sparsely-populated
 * deals.
 */
const DealHeaderCard = ({ deal }) => {
  // The deal row carries the brokerage FK (`brokerage_id`), not a `broker`
  // string, so resolve the name via the brokers_xref lookup (the same map the
  // Overview "Broker" section uses). Falls back to any raw `broker` field.
  const { labelByLookup } = useLookupLabels();
  const summary = useDealHeaderSummary(deal, labelByLookup?.entities);
  if (!deal) return null;
  const brokerName =
    labelByLookup?.brokers_xref?.[deal.brokerage_id] ?? deal.broker;
  const items = [
    { label: "Deal Key", value: deal.deal_key },
    { label: "Status", value: deal.deal_status ?? deal.status },
    { label: "Deal Type", value: deal.deal_type },
    { label: "Broker", value: brokerName },
    { label: "Seller", value: summary.seller },
    { label: "County", value: summary.county },
    { label: "State", value: summary.state },
    { label: "Effective Date", value: formatDateOnly(summary.effectiveDate) },
  ];
  return (
    <Card size="small" style={{ marginBottom: 12 }}>
      <Descriptions column={{ xs: 1, sm: 2, md: 4 }} size="small">
        {items.map(({ label, value }) => (
          <Descriptions.Item key={label} label={label}>
            {value === null || value === undefined || value === "" ? (
              <span style={MUTED_STYLE}>—</span>
            ) : (
              String(value)
            )}
          </Descriptions.Item>
        ))}
      </Descriptions>
    </Card>
  );
};

const DealDetail = () => {
  const { dealKey } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { token, getToken, isTokenExpired, user } = useContext(AppContext);
  const perms = usePermissions();
  const { can } = perms;
  // An external brokerage never sees WIG's promote economics. Narrowed once
  // here so every tab fed from `metadata` inherits it — the Overview form, the
  // read-only child tables, and the field list the save body is built from.
  const isBrokerAgent = isExternalBrokerAgent(perms);

  const [deal, setDeal] = useState(null);
  const [rawMetadata, setMetadata] = useState([]);
  // AOI rides along: required for a broker-submitted deal, because a deal
  // with no AOI is invisible to a broker-lead-tier user and the server
  // refuses to accept one (see utils/brokerRequiredAoi).
  const metadata = useMemo(
    () =>
      withBrokerRequiredAoi(
        filterBrokerHiddenFields(rawMetadata, "deals", isBrokerAgent),
        isBrokerAgent
      ),
    [rawMetadata, isBrokerAgent]
  );
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);
  // True while the Overview tab has an unsaved in-place edit — drives the
  // "discard changes?" confirm on tab switch and back navigation.
  const [unsavedEdits, setUnsavedEdits] = useState(false);
  // Delete-this-deal flow (typed-confirmation modal). `deleteError` keeps the
  // modal open on failure so the user reads the server's reason in place.
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const canDelete = can("deal.delete");

  // Returns true if it's safe to navigate away (no unsaved edits, or the user
  // confirmed discarding them).
  const confirmDiscard = useCallback(
    () =>
      !unsavedEdits ||
      window.confirm(
        "You have unsaved changes to this deal. Leave without saving?"
      ),
    [unsavedEdits]
  );

  // Tabs whose `visibleFor` predicate rejects this deal are dropped entirely
  // (today: Recordings on a non-assignment deal). `deal` is null while the
  // fetch is in flight, but the tab strip only renders after it resolves, so
  // a conditional tab never flickers in. A tab with a `permission` is dropped
  // for a caller without it.
  const visibleTabs = useMemo(
    () =>
      TABS.filter(
        (t) =>
          (!t.visibleFor || t.visibleFor(deal)) &&
          (!t.permission || can(t.permission))
      ),
    [deal, can]
  );

  // URL-driven tab state. Default to "overview"; ignore unknown values, and
  // values naming a tab that's hidden for this deal (a stale ?tab= link, or a
  // deal whose type changed after the link was shared).
  const requestedTab = searchParams.get("tab") || "overview";
  const activeTab =
    VALID_TAB_KEYS.has(requestedTab) &&
    visibleTabs.some((t) => t.key === requestedTab)
      ? requestedTab
      : "overview";

  const onTabChange = (key) => {
    // Switching tabs destroys the (destroyInactiveTabPane) Overview edit form,
    // so confirm before discarding an unsaved edit.
    if (!confirmDiscard()) return;
    const next = new URLSearchParams(searchParams);
    next.set("tab", key);
    setSearchParams(next, { replace: true });
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMsg(null);

      const userOid = user?.idTokenClaims?.oid || user?.localAccountId;
      if (!userOid) throw new Error("OID not found in user claims.");

      let myToken = token;
      if (!myToken || isTokenExpired(myToken)) {
        myToken = await getToken();
      }

      const [dealResp, metaResp] = await Promise.all([
        dealsService.getDeal({ token: myToken, userOid, dealKey }),
        dealsService.getDealsMetadata({ token: myToken, userOid }),
      ]);

      // Single-deal endpoint may return `{ success, data: row }` or just
      // the row itself depending on backend convention; tolerate both.
      const dealRow = dealResp?.data?.data ?? dealResp?.data ?? null;
      setDeal(
        dealRow && typeof dealRow === "object" && !Array.isArray(dealRow)
          ? dealRow
          : Array.isArray(dealRow)
            ? dealRow[0] || null
            : null
      );

      if (metaResp?.data?.data) {
        setMetadata(metaResp.data.data);
      }
    } catch (err) {
      console.error("Error loading deal detail:", err);
      setErrorMsg(err?.message || String(err));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealKey]);

  useEffect(() => {
    if (dealKey) load();
  }, [dealKey, load]);

  // Re-fetch ONLY the deal row, quietly. Tract writes roll up into the deal's
  // derived `deal_net_acres_delivered` (de-hub migration 081), so the header
  // and the Overview tab go stale the moment a tract is added, edited or
  // deleted. `load()` can't be reused for that: it flips `loading`, which
  // swaps the tab area for the skeleton and remounts the tab the user is
  // working in — dropping their selected lease/deed mid-edit. A failure here
  // is not worth an error banner either; the number the user just changed is
  // simply a refresh behind.
  const refreshDealRow = useCallback(async () => {
    try {
      const userOid = user?.idTokenClaims?.oid || user?.localAccountId;
      if (!userOid) return;
      let myToken = token;
      if (!myToken || isTokenExpired(myToken)) {
        myToken = await getToken();
      }
      const resp = await dealsService.getDeal({
        token: myToken,
        userOid,
        dealKey,
      });
      const dealRow = resp?.data?.data ?? resp?.data ?? null;
      if (dealRow && typeof dealRow === "object" && !Array.isArray(dealRow)) {
        setDeal(dealRow);
      }
    } catch (err) {
      console.error("Error refreshing deal row:", err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealKey]);

  // After a successful in-place edit, re-fetch the deal so the header / fields
  // reflect the new values (incl. a renamed deal_key).
  const onSaved = useCallback(
    ({ deal: savedDeal } = {}) => {
      const newKey = savedDeal?.deal_key;
      if (newKey && newKey !== dealKey) {
        // deal_key was renamed — navigate to the new natural-key URL.
        navigate(`/deals/${encodeURIComponent(newKey)}`, { replace: true });
      } else {
        load();
      }
    },
    [dealKey, navigate, load]
  );

  // Open the delete confirm. An unsaved in-place edit is about to become moot,
  // but run the same discard guard rather than silently bypassing it.
  const openDelete = () => {
    if (!confirmDiscard()) return;
    setDeleteError(null);
    setDeleteOpen(true);
  };

  // Delete this deal, then leave for the list. Only reachable once the user has
  // typed the deal key in the confirm modal.
  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      const userOid = user?.idTokenClaims?.oid || user?.localAccountId;
      if (!userOid) throw new Error("OID not found in user claims.");

      let myToken = token;
      if (!myToken || isTokenExpired(myToken)) {
        myToken = await getToken();
      }

      await dealsService.deleteDeal({ token: myToken, userOid, dealKey });

      // The shared axios instance caches GETs for minutes and never
      // self-invalidates on a mutation, so without this the deals list we're
      // about to land on would be served from cache — still listing the deal
      // we just deleted.
      await dealsService.invalidateCache?.();

      message.success(`Deal ${dealKey} deleted`);
      setDeleteOpen(false);
      navigate("/", { replace: true });
    } catch (err) {
      console.error("Error deleting deal:", err);
      // The server guard is the real boundary — the UI gate is cosmetic — so a
      // 403 is an expected outcome here, not a bug. apiErrorText renders it as
      // "You don't have permission to do that." and we stay on the page.
      setDeleteError(errText(err));
    } finally {
      setDeleting(false);
    }
  };

  // The deal-side child-entity tabs need the integer PK (`deals.id`) to
  // hit the nested endpoints, not the natural `deal_key` from the URL.
  const dealId = deal?.id ?? null;

  const renderTab = (t) => {
    switch (t.key) {
      case "overview":
        return (
          <OverviewTab
            deal={deal}
            metadata={metadata}
            onSaved={onSaved}
            onDirtyChange={setUnsavedEdits}
          />
        );
      case "entities":
        return (
          <EntitiesTab
            dealId={dealId}
            dealKey={dealKey}
            onDealDerivedChange={refreshDealRow}
          />
        );
      case "contact_log":
        return (
          <ContactLogTab
            dealId={dealId}
            dealKey={dealKey}
            dealStatus={deal?.deal_status}
            onDealChanged={refreshDealRow}
          />
        );
      case "lease_deeds":
        return (
          <LeaseDeedsTab
            dealId={dealId}
            dealKey={dealKey}
            onDealDerivedChange={refreshDealRow}
          />
        );
      case "wells":
        return <WellsTab dealId={dealId} dealKey={dealKey} />;
      case "map":
        return <DealMap dealId={dealId} />;
      case "status_history":
        return <StatusHistoryTab dealId={dealId} dealKey={dealKey} />;
      case "approvals":
        return (
          <ApprovalsTab
            dealId={dealId}
            dealKey={dealKey}
            dealStatus={deal?.deal_status}
            onDealChanged={refreshDealRow}
          />
        );
      case "assignment_recordings":
        return (
          <AssignmentRecordingsTab dealId={dealId} dealKey={dealKey} deal={deal} />
        );
      case "payments":
        return <PaymentsTab dealId={dealId} dealKey={dealKey} />;
      case "remarks":
        return <RemarksTab dealId={dealId} dealKey={dealKey} />;
      case "attachments":
        return <AttachmentsTab dealId={dealId} dealKey={dealKey} />;
      default:
        return <ComingSoonTab label={t.label} entity={t.placeholder} />;
    }
  };

  const tabItems = useMemo(
    () =>
      visibleTabs.map((t) => ({
        key: t.key,
        label: t.label,
        children: renderTab(t),
      })),
    // renderTab closes over `deal`, `metadata`, `dealId`, `dealKey`, `onSaved`
    // and `refreshDealRow` — when any of those change we want fresh children.
    // ESLint can't see through the helper so we list them explicitly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visibleTabs, deal, metadata, dealId, dealKey, onSaved, refreshDealRow]
  );

  return (
    <React.Fragment>
      <AuthenticatedTemplate>
        <div
          style={{
            padding: 16,
            width: "99vw",
            height: "93vh",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            // Themed layout background (soft gray in light) so the white
            // header + section cards separate instead of blending into a
            // flat white page. Adapts with the theme via the token CSS var.
            background: "var(--ant-color-bg-layout)",
          }}
        >
          {/* Page header — back link + title on the left, destructive action on
              the right. Editing lives in-place on the Deal tab (no drawer).
              Stays fixed while the tab content scrolls. */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
              marginBottom: 12,
              flexShrink: 0,
            }}
          >
            <Space align="center">
              <Button
                type="link"
                icon={<ArrowLeftOutlined />}
                onClick={() => confirmDiscard() && navigate("/")}
              >
                Back to Deals
              </Button>
              <Title level={4} style={{ margin: 0 }}>
                {loading ? "Loading…" : `Deal ${dealKey}`}
              </Title>
            </Space>
            {canDelete && !loading && deal && (
              <Button danger icon={<DeleteOutlined />} onClick={openDelete}>
                Delete deal
              </Button>
            )}
          </div>

          {errorMsg && (
            <Alert
              type="error"
              showIcon
              message="Failed to load deal"
              description={errorMsg}
              style={{ marginBottom: 12, flexShrink: 0 }}
            />
          )}

          {loading ? (
            <Skeleton active paragraph={{ rows: 6 }} />
          ) : (
            <>
              <div style={{ flexShrink: 0 }}>
                <DealHeaderCard deal={deal} />
              </div>
              <Tabs
                className="deal-detail-tabs"
                style={{ flex: 1, minHeight: 0 }}
                activeKey={activeTab}
                onChange={onTabChange}
                items={tabItems}
                destroyInactiveTabPane
              />
            </>
          )}

          <DeleteDealsConfirmModal
            open={deleteOpen}
            dealKeys={[dealKey]}
            submitting={deleting}
            errorMsg={deleteError}
            onCancel={() => setDeleteOpen(false)}
            onConfirm={handleDelete}
          />
        </div>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <SignIn />
      </UnauthenticatedTemplate>
    </React.Fragment>
  );
};

export default DealDetail;
